import {
  ConflictError,
  DomainError,
  ForbiddenError,
  InfrastructureError,
  NotFoundError,
  UnauthenticatedError,
  ValidationError,
} from '@domain/errors';

export interface HttpClientOptions {
  baseUrl: string;
  timeoutMs?: number;
  getAccessToken?: () => string | null;
  onUnauthenticated?: () => void;
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

interface ApiErrorBody {
  message?: string;
  code?: string;
  fields?: Record<string, string>;
}

/**
 * Client fetch minimaliste : sérialise la query, injecte le Bearer JWT
 * et traduit chaque statut HTTP en erreur du domaine.
 */
export class HttpClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(private readonly options: HttpClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, options);
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  private buildUrl(path: string, query?: RequestOptions['query']): string {
    const url = new URL(`${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

    const token = this.options.getAccessToken?.() ?? null;

    try {
      const response = await fetch(this.buildUrl(path, options.query), {
        method,
        signal: controller.signal,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });

      if (response.status === 204) return undefined as T;

      const payload = await this.parseBody(response);

      if (!response.ok) {
        throw this.toDomainError(response.status, payload as ApiErrorBody);
      }
      return payload as T;
    } catch (error) {
      if (error instanceof DomainError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new InfrastructureError('Délai dépassé, réessayez.', 504);
      }
      throw new InfrastructureError('Serveur injoignable, vérifiez votre connexion.');
    } finally {
      clearTimeout(timer);
    }
  }

  private async parseBody(response: Response): Promise<unknown> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return { message: await response.text() };
    }
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  private toDomainError(status: number, body: ApiErrorBody): DomainError {
    const message = body.message;
    switch (status) {
      case 400:
      case 422:
        return new ValidationError(message ?? 'Données invalides', body.fields ?? {});
      case 401:
        this.options.onUnauthenticated?.();
        return new UnauthenticatedError(message);
      case 403:
        return new ForbiddenError(message);
      case 404:
        return new NotFoundError('Ressource', body.code ?? '—');
      case 409:
        return new ConflictError(message);
      default:
        if (status >= 500) return new InfrastructureError(message, status);
        return new DomainError(message ?? 'Requête refusée', status, body.code ?? 'HTTP_ERROR');
    }
  }
}
